import { FC, UIEvent, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import download from 'downloadjs';
import throttle from 'lodash/throttle';

import { Button, Divider, Icon, Spinner, Stack, useScreenSize } from '@servicetitan/design-system';

import { Zoom } from './zoom';
import { Pagination } from './pagination';
import { setPdfWorker } from './pdf-viewer-configuration';
import * as Styles from './pdf-viewer.module.less';

setPdfWorker();

/*
 * Defense-in-depth: the app renders externally-sourced, untrusted PDFs (equipment
 * manuals fetched via a backend proxy). Explicitly disable pdf.js's eval-based
 * font-matrix fast path regardless of pdfjs-dist version. Must be a stable/module-level
 * object reference - react-pdf's <Document> reloads if `options` identity changes on
 * every render.
 */
const pdfDocumentOptions = { isEvalSupported: false };

interface PdfViewerProps {
    file: string | Blob;
    fileName: string;
    pageNumber: number;
    scrollContainerClassName?: string;
    className?: string;
    actions?: JSX.Element;
}

export const PdfViewer: FC<PdfViewerProps> = props => {
    const { scrollContainerClassName, file, className, actions, fileName, pageNumber } = props;
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState<number>(1);
    const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
    const screenSize = useScreenSize();

    const { current: originalWidthPerPage } = useRef(new Map<number, number>());
    const { current: originalHeightPerPage } = useRef(new Map<number, number>());

    const { current: pdfPageClassPrefix } = useRef(
        'pdf_viewer_' + (Math.floor(Math.random() * 90000) + 10000) + '_page_'
    );

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        if (pageNumber > 0) {
            setTimeout(() => {
                handlePageChange(pageNumber);
            }, 1000);
        }
    };

    const handleZoomChange = (value: number) => {
        setZoom(value);
    };

    const handlePageChange = (page: number) => {
        const pageElement = document.querySelector(`.${pdfPageClassPrefix + page}`);
        pageElement?.scrollIntoView({ behavior: 'auto' });
        setCurrentPage(page);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScroll = useCallback(
        throttle((e: UIEvent<HTMLDivElement>) => {
            const div = e.currentTarget;
            const { top: topOffset } = div.getBoundingClientRect();
            const pages = div.getElementsByClassName('react-pdf__Page');

            let newPage = 1;

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const rect = page.getBoundingClientRect();
                const offsetFromWindowTop = rect.top - topOffset;
                if (offsetFromWindowTop > 0) {
                    break;
                }
                newPage = i + 1;
            }

            // if there is no more scroll available, set the last page
            if (
                newPage !== numPages &&
                Math.abs(div.scrollHeight - div.scrollTop - div.clientHeight) < 1
            ) {
                newPage = numPages;
            }

            setCurrentPage(newPage);
        }, 16),
        [numPages]
    );

    const handleFitToWidth = () => {
        const viewportWidth = scrollRef?.clientWidth ?? 1;
        const originalWidth = originalWidthPerPage.get(currentPage) ?? 1;
        setZoom(viewportWidth / originalWidth);
    };
    const handleFitToHeight = () => {
        const viewportHeight = scrollRef?.clientHeight ?? 1;
        const originalHeight = originalHeightPerPage.get(currentPage) ?? 1;
        setZoom(viewportHeight / originalHeight);
    };

    const handlePageRenderSuccess = useCallback(
        ({
            originalWidth,
            originalHeight,
            pageNumber,
        }: {
            originalWidth: number;
            originalHeight: number;
            pageNumber: number;
        }) => {
            if (originalWidth > 0) {
                originalWidthPerPage.set(pageNumber, originalWidth);
            }
            if (originalHeight > 0) {
                originalHeightPerPage.set(pageNumber, originalHeight);
            }
        },
        [originalHeightPerPage, originalWidthPerPage]
    );

    const handleFileDownload = useCallback(() => {
        if (!file) {
            return;
        }
        download(file, fileName);
    }, [file, fileName]);

    // const isCordovaApp = !!(window as any).webkit?.messageHandlers?.cordova_iab;
    const isCordovaApp = true;

    const isMobile = screenSize.smallerThan.mobile;

    return (
        <Stack
            direction="column"
            className={classNames('bg-neutral-20', className, Styles.pdfViewer)}
            data-qa="pdf-viewer"
            style={{ position: 'relative', height: '100%' }} // ensure stacking context and full height
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={classNames('p-y-1', isMobile ? 'p-x-2' : 'p-x-3', 'document-header')}
                style={{
                    position: 'sticky', // or 'fixed' if you want it outside the scroll area
                    top: 0,
                    zIndex: 10,
                    background: '#fff', // or use theme color
                }}
            >
                <Pagination
                    totalPages={numPages}
                    currentPage={currentPage}
                    onJumpToPage={handlePageChange}
                />

                <Zoom
                    value={zoom}
                    onChange={handleZoomChange}
                    onFitToWidth={handleFitToWidth}
                    onFitToHeight={handleFitToHeight}
                />

                <Stack spacing={2} alignItems="center">
                    {!isCordovaApp && (
                        <Button
                            className="bg-neutral-0 border"
                            onClick={handleFileDownload}
                            icon={<Icon name="file_download" />}
                            data-qa="btn-download"
                            size={isMobile ? 'small' : 'medium'}
                        />
                    )}
                    {actions}
                </Stack>
            </Stack>
            <Divider
                style={{
                    position: 'sticky',
                    top: isMobile ? 48 : 56, // adjust based on header height
                    zIndex: 9,
                }}
            />
            <div
                ref={setScrollRef}
                className={classNames(
                    'of-y-auto flex-grow-1  flex-shrink-1 flex-basis-auto bg-neutral-20-i',
                    scrollContainerClassName
                )}
                onScroll={handleScroll}
                data-qa="pdf-viewer-content"
                style={{
                    paddingTop: isMobile ? 0 : 0, // match header height
                    height: '100%',
                }}
            >
                <Document
                    file={file}
                    options={pdfDocumentOptions}
                    loading={
                        <Stack className="h-100 m-t-3" alignItems="center" justifyContent="center">
                            <Spinner size="medium" />
                        </Stack>
                    }
                    onLoadSuccess={onDocumentLoadSuccess}
                >
                    {/* eslint-disable-next-line prefer-spread */}
                    {Array.apply(null, Array(numPages))
                        .map((x, i) => i + 1)
                        .map(page => (
                            <Page
                                key={page}
                                className={classNames(
                                    'bg-neutral-20-i d-f justify-content-center',
                                    {
                                        [`${pdfPageClassPrefix}${page}`]: true,
                                    }
                                )}
                                scale={zoom}
                                renderAnnotationLayer={false}
                                pageNumber={page}
                                onRenderSuccess={handlePageRenderSuccess}
                            />
                        ))}
                </Document>
            </div>
        </Stack>
    );
};
